import { useEffect, useState } from "react";
import LoginScreen from "./screens/LoginScreen";
import "./App.scss";
import { Router, Route, Switch, useHistory } from "react-router-dom";
import PrivateRoutesWrapper from "./screens/PrivateRoutesWrapper";
import useAuthState from "./hooks/useAuthState";
import useUIState from "./hooks/useUIstate";
import LoadingModal from "./components/LoadingModal";
import CustomSnackbar from "./components/CustomSnackBar";
import { API, Auth } from "aws-amplify";
import { FlagsProvider } from "flagged";
import { clearStorage } from './helpers/storage';
import { backofficeLanguage as globalBackofficeLanguage, languages as globalLanguages } from "./statemanagement/stateManagement";
import i18n from "./utilities/i18n";

function App() {
  return (
      <MainApp />
  );
}

function MainApp() {
  const { AuthStatus, setAuthStatus, setUser } = useAuthState();
  const history = useHistory();
  const {
    showLoadingModal,
    toastType,
    toastMessage,
    showToastMessage,
    setShowToastMessage,
  } = useUIState();

  const [EnabledFeatures, setEnabledFeatures] = useState([]);
  const [translationsLoaded, setTranslationsLoaded] = useState(false); // 👈 NEW

  // --- 1. Check authentication on mount ---
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setAuthStatus("loggedIn");
        setUser(user);
      } catch (err) {
        console.error(err);
        clearStorage();
        setAuthStatus("noUser");
        history.push("/login");
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCustomerConfig = async () => {
      if (AuthStatus !== "loggedIn") {
        var backofficeLanguage = localStorage.getItem("backofficeLanguage") ? localStorage.getItem("backofficeLanguage") : 'nl';
        await fetchAllTranslations([backofficeLanguage], backofficeLanguage);
        setTranslationsLoaded(true); // 👈 set flag once done
        return;
      }

      try {
        // --- 2. Fetch config + translations after login ---
        const res = await API.get("AE", `/Stores/WhoAmI`, { response: true });

        localStorage.removeItem("UserConfigData");
        localStorage.setItem("UserConfigData", JSON.stringify(res.data));

        setEnabledFeatures(res.data.EnabledFeatures);

        const langs = res.data.SupportedLanguages || [];
        const boLang =
          res.data.BackofficeLanguage &&
          res.data.BackofficeLanguage.trim() !== ""
            ? res.data.BackofficeLanguage
            : "nl";

        globalLanguages.value = langs;
        globalBackofficeLanguage.value = boLang;

        // ✅ Wait for translations
        await fetchAllTranslations(langs, boLang);
        setTranslationsLoaded(true); // 👈 set flag once done


      } catch (error) {
        console.error("Failed to fetch customer config", error);
      }
    };

    fetchCustomerConfig();
  }, [AuthStatus]);

  // --- Helpers ---
  const fetchTranslations = async (language) => {
    try {
      const res = await fetch(`/global-bo-${language}.json`);
      const data = await res.json();
      i18n.addResourceBundle(language, "combined", data);
    } catch (error) {
      console.error(`Error fetching translations for ${language}`, error);
    }
  };

  const fetchAllTranslations = async (langs, boLang) => {
    if (langs.length > 0) {
      for (const lang of langs) {
        await fetchTranslations(lang);
      }
    } else {
      await fetchTranslations(boLang);
    }

    localStorage.setItem("backofficeLanguage", boLang); // set login language
    await i18n.changeLanguage(boLang);
  };

  // --- Render ---
  if (!translationsLoaded) {
    // ⏳ Show a loading indicator until translations are ready
    return <LoadingModal />;
  }

  return (
    <FlagsProvider features={EnabledFeatures}>
      <div className="App">
        <Router history={history}>
          <Switch>
            {AuthStatus === "loggedIn" && <PrivateRoutesWrapper />}
            {AuthStatus !== "loggedIn" && (
              <Route path="/login" component={LoginScreen} />
            )}
          </Switch>
        </Router>

        {showLoadingModal && <LoadingModal />}
        {showToastMessage && toastMessage && (
          <CustomSnackbar
            open={showToastMessage}
            toastMessage={toastMessage}
            toastType={toastType}
            handleClose={() => setShowToastMessage(false)}
          />
        )}
      </div>
    </FlagsProvider>
  );
}

export default App;
